import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "Full Stack",
    status: "Completed",
    description: "",
    technologies: [],
    link: "",
    imageUrl: "",
    githubUrl: "",
    liveUrl: "",
    featured: false,
    order: 0,
  });
  const [techInput, setTechInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [customCategories, setCustomCategories] = useState([
    "ML",
    "Full Stack",
    "Tools",
    "Java",
  ]);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      if (response.data.success) {
        setProjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      category: "Full Stack",
      status: "Completed",
      description: "",
      technologies: [],
      link: "",
      imageUrl: "",
      githubUrl: "",
      liveUrl: "",
      featured: false,
      order: 0,
    });
    setEditingId(null);
    setTechInput("");
  };

  const handleEdit = (project) => {
    setFormData({
      title: project.title || "",
      category: project.category || "Full Stack",
      status: project.status || "Completed",
      description: project.description || "",
      technologies: project.technologies || [],
      link: project.link || "",
      imageUrl: project.imageUrl || "",
      githubUrl: project.githubUrl || "",
      liveUrl: project.liveUrl || "",
      featured: project.featured || false,
      order: project.order || 0,
    });
    setEditingId(project._id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("adminToken");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingId) {
        await axios.put(`${API_URL}/projects/${editingId}`, formData, config);
        setMessage({ type: "success", text: "Project updated successfully!" });
      } else {
        await axios.post(`${API_URL}/projects`, formData, config);
        setMessage({ type: "success", text: "Project created successfully!" });
      }

      fetchProjects();
      resetForm();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Operation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;

    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API_URL}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: "success", text: "Project deleted successfully!" });
      fetchProjects();
    } catch (error) {
      setMessage({ type: "error", text: "Delete failed" });
    }
  };

  const addTechnology = () => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, techInput.trim()],
      });
      setTechInput("");
    }
  };

  const removeTechnology = (index) => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6">
        Manage Projects
      </h2>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6 bg-white/[0.03] border border-white/[0.08] rounded-lg sm:rounded-xl p-4 sm:p-6"
      >
        <h3 className="text-lg sm:text-xl font-light">
          {editingId ? "Edit Project" : "Add New Project"}
        </h3>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                     focus:outline-none focus:border-white/30 transition-all"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-white/60 text-xs sm:text-sm mb-2">
              Category
            </label>
            <div className="space-y-2">
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                         focus:outline-none focus:border-white/30 transition-all"
                style={{ colorScheme: "dark" }}
                required
              >
                {customCategories.map((cat) => (
                  <option
                    key={cat}
                    value={cat}
                    className="bg-gray-900 text-white"
                  >
                    {cat}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowAddCategory(!showAddCategory)}
                className="text-xs text-white/40 hover:text-white/60 transition-colors"
              >
                {showAddCategory ? "- Cancel" : "+ Add New Category"}
              </button>
              {showAddCategory && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="New category name"
                    className="flex-1 px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white text-sm
                             focus:outline-none focus:border-white/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        newCategoryInput.trim() &&
                        !customCategories.includes(newCategoryInput.trim())
                      ) {
                        setCustomCategories([
                          ...customCategories,
                          newCategoryInput.trim(),
                        ]);
                        setFormData({
                          ...formData,
                          category: newCategoryInput.trim(),
                        });
                        setNewCategoryInput("");
                        setShowAddCategory(false);
                      }
                    }}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all text-xs"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-white/60 text-xs sm:text-sm mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
              style={{ colorScheme: "dark" }}
              required
            >
              <option value="Completed" className="bg-gray-900 text-white">
                Completed
              </option>
              <option value="Ongoing" className="bg-gray-900 text-white">
                Ongoing
              </option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows="4"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                     focus:outline-none focus:border-white/30 transition-all resize-none"
            required
          />
        </div>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            Technologies
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={techInput}
              onChange={(e) => setTechInput(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), addTechnology())
              }
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
              placeholder="Add technology"
            />
            <button
              type="button"
              onClick={addTechnology}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl hover:bg-white/20 transition-all text-xs sm:text-sm"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.technologies.map((tech, index) => (
              <div
                key={index}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg flex items-center gap-2 text-xs sm:text-sm"
              >
                <span>{tech}</span>
                <button
                  type="button"
                  onClick={() => removeTechnology(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-white/60 text-xs sm:text-sm mb-2">
            GitHub/Project Link
          </label>
          <input
            type="text"
            value={formData.link || formData.githubUrl}
            onChange={(e) =>
              setFormData({
                ...formData,
                link: e.target.value,
                githubUrl: e.target.value,
              })
            }
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                     focus:outline-none focus:border-white/30 transition-all"
            placeholder="https://github.com/username/project"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-white/60 text-xs sm:text-sm mb-2">
              Image URL (Optional)
            </label>
            <input
              type="text"
              value={formData.imageUrl}
              onChange={(e) =>
                setFormData({ ...formData, imageUrl: e.target.value })
              }
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
            />
          </div>

          <div>
            <label className="block text-white/60 text-xs sm:text-sm mb-2">
              Live Demo URL (Optional)
            </label>
            <input
              type="text"
              value={formData.liveUrl}
              onChange={(e) =>
                setFormData({ ...formData, liveUrl: e.target.value })
              }
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-white/60 text-xs sm:text-sm mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) =>
                setFormData({ ...formData, order: parseInt(e.target.value) })
              }
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.05] border border-white/[0.1] rounded-lg sm:rounded-xl text-white text-sm sm:text-base
                       focus:outline-none focus:border-white/30 transition-all"
            />
          </div>

          <div className="flex items-center">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) =>
                  setFormData({ ...formData, featured: e.target.checked })
                }
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
              <span className="text-white/60 text-xs sm:text-sm">
                Featured Project
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl hover:bg-white/20 
                     transition-all disabled:opacity-50 text-sm sm:text-base"
          >
            {loading ? "Saving..." : editingId ? "Update" : "Create"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="w-full sm:w-auto px-6 py-3 bg-red-500/20 border border-red-500/30 rounded-lg sm:rounded-xl hover:bg-red-500/30 transition-all text-sm sm:text-base"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {message.text && (
        <div
          className={`p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* List */}
      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-lg sm:text-xl font-light">Existing Projects</h3>
        {projects.map((project) => (
          <div
            key={project._id}
            className="bg-white/[0.03] border border-white/[0.08] rounded-lg sm:rounded-xl p-4 sm:p-6 hover:border-white/15 transition-all"
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-lg font-medium">{project.title}</h4>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                    {project.category}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      project.status === "Ongoing"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-gray-500/20 text-gray-400"
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
                {project.featured && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded mt-1 inline-block">
                    Featured
                  </span>
                )}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-all text-xs sm:text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(project._id)}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all text-xs sm:text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
            <p className="text-white/70 mb-3">{project.description}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {project.technologies.map((tech, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
            <div className="flex gap-4 text-sm text-white/60">
              {(project.link || project.githubUrl) && (
                <a
                  href={project.link || project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  GitHub/Project Link →
                </a>
              )}
              {project.liveUrl && (
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  Live Demo →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProjectsAdmin;
